import {
  STOCK_ADJUSTMENT_STATUS_LABELS,
  type StockAdjustment,
} from "@artisancode/api-types";
import { AlertCircle, Ban, Package, Wheat } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateWIB } from "@/lib/date-utils";

import { StockAdjustmentDiffBadge } from "./stock-adjustment-diff-badge";
import { getBucketLabel } from "./stock-adjustment-utils";

export interface StockAdjustmentRowsProps {
  rows: StockAdjustment[];
  onCancelRequest: (adj: StockAdjustment) => void;
}

export function StockAdjustmentDesktopTable({
  rows,
  onCancelRequest,
}: StockAdjustmentRowsProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead className="w-[100px]">Tanggal</TableHead>
            <TableHead>No. Dokumen</TableHead>
            <TableHead>Tipe</TableHead>
            <TableHead>Barang</TableHead>
            <TableHead>Lokasi</TableHead>
            <TableHead className="text-right">Sistem</TableHead>
            <TableHead className="text-right">Fisik</TableHead>
            <TableHead className="text-center">Selisih</TableHead>
            <TableHead>Alasan</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={11}
                className="h-32 text-center text-xs text-muted-foreground"
              >
                <div className="flex flex-col items-center justify-center gap-1">
                  <AlertCircle className="h-6 w-6 text-muted-foreground/50" />
                  <span>Belum ada data penyesuaian stok.</span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((adj) => {
              const statusMeta = STOCK_ADJUSTMENT_STATUS_LABELS[adj.status];

              return (
                <TableRow key={adj.id} className="text-xs">
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDateWIB(adj.date)}
                  </TableCell>
                  <TableCell className="font-mono font-medium">
                    {adj.code}
                  </TableCell>
                  <TableCell>
                    {adj.itemType === "raw_material" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                        <Wheat className="h-3 w-3" />
                        Bahan Baku
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 dark:text-blue-400">
                        <Package className="h-3 w-3" />
                        Produk
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-foreground">
                      {adj.itemName}
                    </div>
                    {adj.note && (
                      <div className="max-w-[200px] truncate text-[11px] italic text-muted-foreground">
                        {adj.note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-[11px] text-muted-foreground">
                      {getBucketLabel(adj.bucket)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {adj.systemQty} {adj.uomName}
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {adj.countedQty} {adj.uomName}
                  </TableCell>
                  <TableCell className="text-center">
                    <StockAdjustmentDiffBadge
                      difference={adj.difference}
                      uomName={adj.uomName}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-[11px]">{adj.reasonName || "-"}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusMeta.variant} className="text-[10px]">
                      {statusMeta.label}
                    </Badge>
                    {adj.cancelReason && (
                      <span
                        className="block max-w-[120px] truncate text-[10px] text-muted-foreground"
                        title={adj.cancelReason}
                      >
                        Ket: {adj.cancelReason}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {adj.status === "completed" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onCancelRequest(adj)}
                        className="h-7 px-2 text-[11px] text-destructive hover:bg-destructive/10 hover:text-destructive"
                        title="Batalkan penyesuaian"
                      >
                        <Ban className="mr-1 h-3 w-3" />
                        Batalkan
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
